import {useCallback} from 'react';

import {find} from 'lodash';
import {useDispatch, useSelector} from 'react-redux';

import {selectRestaurantsMainList} from '~/shared/store/selectors';
import apiService from '~/shared/services/apiService';
import {api} from '~/shared/services/bffService';
import {PaymentMethodTypes} from '~/shared/consts/paymentConsts';
import {fetchOrderSummaryIfNeeded, setCurrentModal} from '~/shared/store/actions';
import {secureOpenWindow, setIn} from '~/shared/utils/general';
import {ActiveOrderState, OrderData} from '~/shared/store/models';
import {createLogger} from '~/shared/logging';
import {EMPTY_OBJECT} from '~/shared/consts/commonConsts';
import {pushRoute, useLocation} from '~/shared/router';
import {routeNames} from '~/shared/routes';
import {trackEvent} from '~/shared/services/analytics';
import {useIsMinTablet} from '~/shared/hooks/deviceInfo';
import {convertRestaurant} from '~/shared/utils/restaurant';
import {handleRefreshToken} from '~/shared/services/auth';
import {is401Error} from '~/shared/services/apiErrorService';
import {OrderWithMenuVisibility} from '~/shared/store/models/User/UserReport';

const logger = createLogger('useSideMenuHandlers');

type SideMenuHandlersProps = {
  data: Record<string, any>;
  setData: (args: Record<string, any>) => void;
  dateBias: number | string;
};

type RestaurantReviewResponse = {
  resReviewId: string;
  reviewText: string;
  ranking: number;
};

type DebitTransferProps = {
  amount: number;
  notes: string;
  transferReceiverEmail: string;
};

export type SideMenuOrder = ActiveOrderState | (OrderWithMenuVisibility & {id: number});

const useSideMenuHandlers = (params?: SideMenuHandlersProps) => {
  const dispatch = useDispatch();
  const {routeName} = useLocation();
  const restaurantList = useSelector(selectRestaurantsMainList);
  const shouldOpenModal = useIsMinTablet();
  const hideReorderButton = routeName === routeNames.personalArea;

  const onShowOrderClick = useCallback(
    async (order: SideMenuOrder) => {
      const {id: orderId, restaurantId} = order;
      trackEvent('hasClickedViewOrder', {
        orderHistoryTransactionID: orderId,
        restaurantID: restaurantId,
        orderPositionInList: null,
      });

      const fetchOrderSummery = async () => {
        dispatch(setCurrentModal('order_summary_modal', {hideReorderButton}));
        await dispatch(fetchOrderSummaryIfNeeded({orderId}));
      };

      if (shouldOpenModal) {
        try {
          await fetchOrderSummery();
          return;
        } catch (error) {
          if (is401Error(error)) {
            await handleRefreshToken(error, fetchOrderSummery);
            return;
          }

          throw error;
        }
      }
      pushRoute(`/order-summary/${orderId}`);
    },
    [dispatch, hideReorderButton, shouldOpenModal],
  );

  const onAddEditReviewClick = useCallback(
    async (order: SideMenuOrder) => {
      const {restaurantId, reviewTransactionId, id: orderId, review, orderType} = order;
      const orderTypeOrigin = 'orderTypeOrigin' in order ? order.orderTypeOrigin : undefined;

      trackEvent('enterFeedback_click', {
        orderHistoryTransactionID: orderId,
        restaurantID: restaurantId,
        orderPositionInList: null,
      });

      dispatch(
        setCurrentModal('addRestaurantReview', {
          restaurant: convertRestaurant(find(restaurantList, {restaurantId})) || await api.fetchRestaurant({id: restaurantId}),
          transactionId: reviewTransactionId,
          transactionType: orderTypeOrigin || orderType,
          orderHistoryTransactionID: orderId,
          review,
          onSubmit: (res: RestaurantReviewResponse) => {
            if (!params) {
              // useSideMenuHandlers used in User Report - and there we have data/setData props.
              return null;
            }
            const {data, setData, dateBias} = params;
            const {resReviewId: newReviewId, reviewText: newReviewText, ranking: newRanking} = res || {};
            const dataWithUpdatedReview = setIn(
              data,
              [dateBias, 'orderList'],
              [
                ...data[dateBias].orderList.map((currentOrder: OrderData) => {
                  if (currentOrder.orderId === Number(orderId)) {
                    return {
                      ...currentOrder,
                      review: {
                        ...currentOrder.review,
                        reviewText: newReviewText || currentOrder.review.reviewText,
                        reviewId: newReviewId || currentOrder.review.reviewId,
                        reviewRank: newRanking || currentOrder.review.reviewRank,
                      },
                    };
                  }

                  return currentOrder;
                }),
              ],
            );
            setData(dataWithUpdatedReview);
          },
        }),
      );
    },
    [dispatch, params, restaurantList],
  );

  const onAddDisplayReviewClick = useCallback(
    async (order: SideMenuOrder) => {
      const {restaurantId, reviewTransactionId, review, orderType, orderId} = order;
      const orderTypeOrigin = 'orderTypeOrigin' in order ? order.orderTypeOrigin : undefined;

      dispatch(
        setCurrentModal('addRestaurantReview', {
          restaurant: convertRestaurant(find(restaurantList, {restaurantId})) || await api.fetchRestaurant({id: restaurantId}),
          transactionId: reviewTransactionId,
          orderHistoryTransactionID: orderId,
          transactionType: orderTypeOrigin || orderType,
          isEditDisabled: true,
          review,
        }),
      );
    },
    [dispatch, restaurantList],
  );

  const onDebitTransferClick = useCallback(
    (order: SideMenuOrder) => {
      const onDebitTransferSubmit = async ({amount, notes, transferReceiverEmail}: DebitTransferProps) => {
        const payment = find(order?.payments, {
          paymentMethod: PaymentMethodTypes.MONEY_CARD,
        });

        try {
          const res = await apiService.insertTransferDebitRequest({
            transactionId: payment?.transactionId,
            moneycarId: payment?.paymentId,
            targetUserEmail: transferReceiverEmail,
            transferDebitSum: amount,
            transferDebitComments: notes || '',
          });

          if (!params) {
            // useSideMenuHandlers used in User Report - and there we have data/setData props.
            return;
          }

          const {data, setData, dateBias} = params || EMPTY_OBJECT;

          const dataWithNewDebitTransferSentRequest = setIn(
            data,
            [dateBias, 'debitTransfers', 'debitTransfersFromUser'],
            [
              ...data[dateBias].debitTransfers.debitTransfersFromUser,
              {
                ...res.data,
                orderId: order.id || order.orderId,
              },
            ],
          );

          setData(dataWithNewDebitTransferSentRequest);
        } catch (error: any) {
          if (is401Error(error)) {
            await handleRefreshToken(error, onDebitTransferSubmit, {amount, notes, transferReceiverEmail});
            return;
          }
          
          logger.error('debit transfer sent request failed', error?.message || error?.data);
        }
      };
      dispatch(
        setCurrentModal('sendDebitTransferRequestModal', {
          order,
          onSubmit: onDebitTransferSubmit,
        }),
      );
    },
    [dispatch, params],
  );

  const onDownloadDealInvoiceClick = useCallback((inoviceUrls: SideMenuOrder['inoviceUrls']) => {
    if (window && inoviceUrls && inoviceUrls.length > 0) {
      inoviceUrls.forEach(url => {
        secureOpenWindow(url, '');
      });
    }
  }, []);

  return {
    onShowOrderClick,
    onAddEditReviewClick,
    onAddDisplayReviewClick,
    onDownloadDealInvoiceClick,
    onDebitTransferClick,
  };
};

export default useSideMenuHandlers;
